import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';

interface MessageModalProps {
  open: boolean;
  onClose: () => void;
  listingId: number;
  listingType: 'supply' | 'demand';
  listingTitle: string;
  receiverName: string;
  receiverId: number;
  onSend: (content: string) => void;
}

export default function MessageModal({
  open,
  onClose,
  listingId,
  listingType,
  listingTitle,
  receiverName,
  receiverId,
  onSend,
}: MessageModalProps) {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const handleSubmit = () => {
    const trimmed = content.trim();
    if (!trimmed || trimmed.length > 500) return;
    setSending(true);
    // simulate brief delay
    setTimeout(() => {
      onSend(trimmed);
      setSent(true);
      setSending(false);
      setContent('');
    }, 400);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-xl w-full max-w-lg mx-4 shadow-2xl animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
          <div>
            <h3 className="font-heading text-base font-bold text-foreground-900">发送站内信</h3>
            <p className="text-foreground-400 text-xs mt-0.5">
              发送给 <span className="text-foreground-700 font-medium">{receiverName}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        {/* Listing reference */}
        <div className="px-6 py-3 bg-background-100/70 border-b border-background-200">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-foreground-400 text-xs">关联挂牌：</span>
            <Link
              to={`/${listingType}/${listingId}`}
              onClick={onClose}
              className="text-primary-600 hover:text-primary-700 font-medium truncate cursor-pointer"
            >
              {listingTitle}
            </Link>
            <span className={`px-1.5 py-0.5 text-xs rounded-full flex-shrink-0 ${
              listingType === 'supply'
                ? 'bg-primary-100 text-primary-700'
                : 'bg-accent-100 text-accent-700'
            }`}>
              {listingType === 'supply' ? '供给' : '需求'}
            </span>
          </div>
        </div>

        {/* Content */}
        {!sent ? (
          <div className="px-6 py-4">
            {!user ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-user-3-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600 text-sm mt-3">请先登录后再发送站内信</p>
                <Link
                  to={`/login?returnUrl=${encodeURIComponent(`/${listingType}/${listingId}`)}`}
                  className="inline-block mt-4 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  前往登录
                </Link>
              </div>
            ) : (
              <>
                <p className="text-foreground-500 text-xs mb-3">
                  以 <span className="font-medium text-foreground-700">{user.companyName}</span> 的身份发送消息
                </p>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  maxLength={500}
                  placeholder={`向 ${receiverName} 发送消息，介绍您的需求和意向…`}
                  className="w-full h-32 px-4 py-3 text-sm text-foreground-900 bg-background-100 rounded-lg border border-background-200 resize-none focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 placeholder:text-foreground-300"
                />
                <div className="flex items-center justify-between mt-3">
                  <span className={`text-xs ${content.length > 500 ? 'text-accent-600' : 'text-foreground-400'}`}>
                    {content.length}/500
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={!content.trim() || content.length > 500 || sending}
                      className="px-5 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {sending ? '发送中...' : '发送'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="px-6 py-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-primary-100 flex items-center justify-center">
              <i className="ri-check-line text-3xl text-primary-600"></i>
            </div>
            <h4 className="font-heading text-lg font-bold text-foreground-900 mt-3">消息已发送</h4>
            <p className="text-foreground-500 text-sm mt-1">
              您的消息已成功发送给 {receiverName}
            </p>
            <div className="flex items-center justify-center gap-3 mt-5">
              <button
                onClick={() => {
                  setSent(false);
                  setContent('');
                }}
                className="px-5 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                继续发送
              </button>
              <button
                onClick={onClose}
                className="px-5 py-2 border border-background-300 text-foreground-600 text-sm font-medium rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                关闭
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}